import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
} from "react";
import {
  CommentModel,
  PlayerMeta,
  PlayerStatus,
  ProductModel,
  PusherConfig,
} from "@/types";
import Pusher from "pusher-js";

interface PlayerContextType {
  meta: PlayerMeta;
  setMeta: (meta: PlayerMeta) => void;
  title: string;
  setTitle: (title: string) => void;
  views: number | null;
  setViews: (views: number | null) => void;
  products: ProductModel[];
  setProducts: (products: ProductModel[]) => void;
  startAt: string;
  setStartAt: (startAt: string) => void;
  thumbnail: string;
  setThumbnail: (thumbnail: string) => void;
  status: PlayerStatus;
  setStatus: (status: PlayerStatus) => void;
  pusher: Pusher | null;
  setPusher: (pusher: Pusher | null) => void;
  pusherConfig: PusherConfig | null;
  setPusherConfig: (config: PusherConfig | null) => void;
  comments: CommentModel[];
  setComments: (
    comments: CommentModel[] | ((prev: CommentModel[]) => CommentModel[])
  ) => void;
  allComments: CommentModel[];
  setAllComments: (
    comments: CommentModel[] | ((prev: CommentModel[]) => CommentModel[])
  ) => void;
  commentScrollEnabled: boolean;
  setCommentScrollEnabled: (enabled: boolean) => void;
  filteredComments: CommentModel[];
  setFilteredComments: (
    comments: CommentModel[] | ((prev: CommentModel[]) => CommentModel[])
  ) => void;
  showComment: boolean;
  setShowComment: (show: boolean) => void;
  showSubtitle: boolean;
  setShowSubtitle: (show: boolean) => void;
  isPlay: boolean;
  setIsPlay: (isPlay: boolean) => void;
  fullScreen: boolean;
  setFullScreen: (fullScreen: boolean) => void;
  isMuted: boolean;
  setIsMuted: (muted: boolean) => void;
  pinnedComment: CommentModel | null;
  setPinnedComment: (comment: CommentModel | null) => void;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export function PlayerProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<PlayerMeta>({});
  const [title, setTitle] = useState<string>("");
  const [views, setViews] = useState<number | null>(null);
  const [products, setProducts] = useState<ProductModel[]>([]);
  const [startAt, setStartAt] = useState<string>("");
  const [thumbnail, setThumbnail] = useState<string>("");
  const [status, setStatus] = useState<PlayerStatus>("loading");
  const [pusher, setPusher] = useState<Pusher | null>(null);
  const [pusherConfig, setPusherConfig] = useState<PusherConfig | null>(null);
  const [comments, setComments] = useState<CommentModel[]>([]);
  const [allComments, setAllComments] = useState<CommentModel[]>([]);
  const [commentScrollEnabled, setCommentScrollEnabled] =
    useState<boolean>(false);
  const [filteredComments, setFilteredComments] = useState<CommentModel[]>([]);
  const [showComment, setShowComment] = useState<boolean>(false);
  const [showSubtitle, setShowSubtitle] = useState<boolean>(false);
  const [isPlay, setIsPlay] = useState<boolean>(false);
  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [pinnedComment, setPinnedComment] = useState<CommentModel | null>(null);

  return (
    <PlayerContext.Provider
      value={{
        meta,
        setMeta,
        title,
        setTitle,
        views,
        setViews,
        products,
        setProducts,
        startAt,
        setStartAt,
        thumbnail,
        setThumbnail,
        status,
        setStatus,
        pusher,
        setPusher,
        pusherConfig,
        setPusherConfig,
        comments,
        setComments,
        allComments,
        setAllComments,
        commentScrollEnabled,
        setCommentScrollEnabled,
        filteredComments,
        setFilteredComments,
        showComment,
        setShowComment,
        showSubtitle,
        setShowSubtitle,
        isPlay,
        setIsPlay,
        fullScreen,
        setFullScreen,
        isMuted,
        setIsMuted,
        pinnedComment,
        setPinnedComment,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const context = useContext(PlayerContext);
  if (context === undefined) {
    throw new Error("usePlayer must be used within a PlayerProvider");
  }
  return context;
}
